import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Bookmark, MoreVertical } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface KnowledgeCardProps {
  id: string;
  title: string;
  summary: string;
  source?: string;
  tags: string[];
  createdAt: Date;
  type: "article" | "note" | "research";
}

export function KnowledgeCard({
  title,
  summary,
  source,
  tags,
  createdAt,
  type,
}: KnowledgeCardProps) {
  return (
    <Card className="p-6 hover-elevate cursor-pointer transition-all" data-testid="card-knowledge">
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-1">
            <h3 className="text-lg font-medium line-clamp-2" data-testid="text-knowledge-title">
              {title}
            </h3>
            {source && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ExternalLink className="h-3 w-3" />
                <span className="truncate" data-testid="text-knowledge-source">{source}</span>
              </div>
            )}
          </div>
          <Button variant="ghost" size="icon" data-testid="button-knowledge-menu">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </div>
        
        <p className="text-sm text-muted-foreground line-clamp-3" data-testid="text-knowledge-summary">
          {summary}
        </p>

        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs" data-testid={`badge-tag-${index}`}>
                {tag}
              </Badge>
            ))}
            {tags.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{tags.length - 3}
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="text-knowledge-date">
            {formatDistanceToNow(createdAt, { addSuffix: true })}
          </span>
        </div>
      </div>
    </Card>
  );
}
